import { Aside } from '@psl/appComponents/Aside';
import { FontFamily } from '@psl/components/styles/Colors';
import { AppBottomSheetProvider } from '@psl/flows/common/app-bottomsheet-flow/context';
import { createStackNavigator } from '@react-navigation/stack';
import { useCallback, useRef } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { BottomSheetWrapper } from '../../../appComponents/BottomSheetWrapper';
import {
  ModalWrapper,
  ModalWrapperProps,
} from '../../../appComponents/ModalWrapper';
import { UserInterfaceStyle } from '../../../appComponents/UserInterfaceIdiomContext';
import LayoutMeasurer from '../../../components/measure/LayoutMeasurerLegacy';
import { useIsMobileLayout } from '../../../components/styles/responsive';
import {
  ScreenToParams,
  spreadScreens,
  wrapNavigatorScreen,
} from '../../../functions/navigation/utils/wrapNavigatorScreen';
import { ChatPage } from '../../community/chat/pages/chat-room/ChatPage';
import { ReportMessagePage } from '../../community/chat/pages/report-message/ReportMessagePage';
import { ChatSettingsPage } from '../../community/chat/pages/settings/ChatSettingsPage';
import { ShareCollectionPage } from '../../community/chat/pages/share-collection/ShareCollectionPage';
import { ShareOrderPage } from '../../community/chat/pages/share-order/ShareOrderPage';
import { ShareStoreListingPage } from '../../community/chat/pages/share-store-listing/ShareStoreListingPage';
import { RequireCustomerTipPage } from '../../community/chat/pages/tips/RequireCustomerTipPage';
import { SellerIsHostingShowTipPage } from '../../community/chat/pages/tips/SellerIsHostingShowTipPage';
import { CommentPage } from '../../community/comments/clip-comment/CommentPage';
import { ShopSearchSettingsPage } from '../../shop/ShopSearchSettingsPage';
import { BadgeDetailPage } from '../badges/BadgeDetailPage';
import { BadgeSharePage } from '../badges/BadgeSharePage';

const Screens = {
  _badgeDetail: BadgeDetailPage,
  _shareBadge: BadgeSharePage,
  _shopSearchSettings: ShopSearchSettingsPage,
  _shareStoreFavoriteList: ShareStoreListingPage,
  _shareOrderList: ShareOrderPage,
  _shareCollectionList: ShareCollectionPage,
  _notCustomerTip: RequireCustomerTipPage,
  _sellerIsHostingShowTip: SellerIsHostingShowTipPage,
  _reportChatMessage: ReportMessagePage,
  _chatSettings: ChatSettingsPage,
  _chatSheet: ChatPage,
  _clipComments: CommentPage,
};

export type AppBottomSheetNavParams = ScreenToParams<typeof Screens>;

const Stack = createStackNavigator<AppBottomSheetNavParams>();

type Props<
  T extends keyof AppBottomSheetNavParams = keyof AppBottomSheetNavParams,
> = {
  initialScreen: T;
  initialParams: AppBottomSheetNavParams[T];
  userInterfaceStyle?: UserInterfaceStyle;
  snapPoints?: number[];
  initialSnapToIndex?: number;
  modalWrapperProps?: ModalWrapperProps;
  cardStyle?: StyleProp<ViewStyle>;
  nonMobilePresentationStyle?: 'modal' | 'aside';
  dismissible?: boolean;
};

// a general global/app level page container which can be used
// to present some non-showcase specific contents in bottom sheet.
// independent navigation container is not used for simplicity now, maybe later...
export const AppBottomSheetFlow = wrapNavigatorScreen(
  ({
    dismissible,
    initialScreen,
    initialParams,
    userInterfaceStyle,
    snapPoints,
    initialSnapToIndex,
    modalWrapperProps,
    cardStyle,
    nonMobilePresentationStyle = 'modal',
  }: Props) => {
    const isMobile = useIsMobileLayout();

    const continueWith = useRef<() => void>();

    const onFinish = useCallback((callback?: () => void) => {
      continueWith.current = callback;
    }, []);

    const onDismiss = useCallback(() => {
      if (continueWith.current) {
        const callback = continueWith.current;
        continueWith.current = undefined;
        setTimeout(callback, 350);
      }
    }, []);

    const content = (
      <LayoutMeasurer>
        <Stack.Navigator
          initialRouteName={initialScreen}
          screenOptions={{
            headerBackTitle: ' ',
            headerTitleAlign: 'center',
            headerTitleStyle: FontFamily.semiBold({
              fontSize: 14,
            }),
            headerStyle: {
              height: 44,
              elevation: 0,
              borderBottomWidth: 0,
            },
            cardStyle: [
              {
                backgroundColor:
                  !isMobile && modalWrapperProps?.backgroundColor
                    ? modalWrapperProps?.backgroundColor
                    : userInterfaceStyle === 'dark'
                      ? '#000'
                      : '#ffffff',
              },
              cardStyle,
            ],
          }}
        >
          {spreadScreens(Stack.Screen, Screens, initialScreen, initialParams)}
        </Stack.Navigator>
      </LayoutMeasurer>
    );

    if (!isMobile) {
      if (nonMobilePresentationStyle === 'modal') {
        return (
          <AppBottomSheetProvider onFinish={onFinish}>
            <ModalWrapper
              dismissible={dismissible ?? true}
              onClose={onDismiss}
              {...modalWrapperProps}
            >
              {content}
            </ModalWrapper>
          </AppBottomSheetProvider>
        );
      }

      return (
        <AppBottomSheetProvider onFinish={onFinish}>
          <Aside dismissible={dismissible ?? true} onDismiss={onDismiss}>
            {content}
          </Aside>
        </AppBottomSheetProvider>
      );
    }
    return (
      <AppBottomSheetProvider onFinish={onFinish}>
        <BottomSheetWrapper
          dismissible={dismissible ?? true}
          userInterfaceStyle={userInterfaceStyle}
          snapPoints={snapPoints}
          initialSnapToIndex={initialSnapToIndex}
          onDismiss={onDismiss}
        >
          {content}
        </BottomSheetWrapper>
      </AppBottomSheetProvider>
    );
  },
  {
    // important to keep compatible w/ other screen presented in modal style
    presentation: 'transparentModal',
    cardOverlayEnabled: true,
    animationEnabled: false,
    detachPreviousScreen: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    headerShown: false,
  },
);
